import React from "react";

/**
 * Watercolor brush-stroke divider — used to separate sections gracefully.
 * Accepts a `color` prop for the stroke tint.
 */
export default function BrushDivider({
  color = "#F5D5D0",
  className = "",
  height = 80,
  variant = "wave",
}) {
  const paths = {
    wave:
      "M2 60 C120 20, 240 80, 380 45 S 620 90, 780 40 S 980 70, 1180 45 S 1380 65, 1518 42",
    swash:
      "M4 40 C120 60, 260 12, 420 55 S 640 95, 820 30 S 1080 55, 1300 40 S 1420 25, 1516 55",
    ribbon:
      "M2 45 Q 300 5 640 45 T 1280 45 T 1518 45",
  };
  return (
    <svg
      viewBox="0 0 1520 80"
      preserveAspectRatio="none"
      className={className}
      style={{ display: "block", width: "100%", height }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`brush-${color.replace('#','')}`} x1="0" x2="1" y1="0.5" y2="0.5">
          <stop offset="0" stopColor={color} stopOpacity="0" />
          <stop offset="0.15" stopColor={color} stopOpacity="0.9" />
          <stop offset="0.85" stopColor={color} stopOpacity="0.9" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id="brush-blur">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>
      <path
        d={paths[variant] || paths.wave}
        stroke={`url(#brush-${color.replace('#','')})`}
        strokeWidth="7"
        strokeLinecap="round"
        fill="none"
        filter="url(#brush-blur)"
      />
    </svg>
  );
}

/**
 * Discrete watercolor touch — a soft blurred spot, like a fingerprint of paint.
 */
export const BrushIcon = ({ className = "", color, index = 0 }) => {
  const palette = ["#F5A5B8", "#FFB894", "#C4B0E8", "#7EC8CE", "#FF8D7B", "#FFDD87", "#B8D480"];
  const c = color || palette[index % palette.length];
  const gid = `wc-${c.replace('#','')}-${index}`;
  return (
    <svg viewBox="0 0 60 60" className={className} aria-hidden="true">
      <defs>
        <radialGradient id={gid} cx="0.4" cy="0.4" r="0.6">
          <stop offset="0" stopColor={c} stopOpacity="0.55" />
          <stop offset="0.6" stopColor={c} stopOpacity="0.25" />
          <stop offset="1" stopColor={c} stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Soft organic watercolor blob */}
      <path
        d="M32 10 C 44 16, 50 26, 48 38 C 45 48, 34 52, 24 48 C 14 44, 8 34, 12 24 C 16 14, 24 8, 32 10 Z"
        fill={`url(#${gid})`}
      />
    </svg>
  );
};

/**
 * Aureole — the soft watercolor round shape from the logo, useful as decoration.
 */
export const Aureole = ({ className = "", size = 260 }) => (
  <div
    className={`aureole ${className}`}
    style={{ width: size, height: size }}
    aria-hidden="true"
  />
);
